import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux"; // ✅ for getting units dynamically
import "./Profile.css";
// Avatar import not currently used (photo UI commented out)
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
// SaveIcon not required currently
import { getUserProfile, updateUserProfile } from "../../../services/clientWebsite";
import { notify } from "../../../components/Toast";
// navigate not required

const initialState = {
  profilePhoto: null,
  name: "",
  email: "",
  phone_number: "",
  height: "",
  height_unit: "",
  weight: "",
  weight_unit: "",
  body_type: "",
};
const bodyTypeOptions = ["Ectomorph", "Mesomorph", "Endomorph"];

const ProfileForm = () => {
  const { height_unit, weight_unit } = useSelector(
    (state) => state.globalSettings
  ); // ✅ get from Redux
  const user = useSelector((state) => state.auth.user);

  const [formData, setFormData] = useState({
    ...initialState,
    height_unit: height_unit || "cm",
    weight_unit: weight_unit || "kg",
  });

  // optional: workout plans not used in this form
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true); // ✅ New: Initial loading state
  const [formErrors, setFormErrors] = useState({ height: "", weight: "" });
  // dispatch/navigate not required in this form currently

  // Validation helpers
  const validateHeight = (value, unit) => {
    if (value === "" || value === null || typeof value === "undefined") return "";
    const num = Number(value);
    if (Number.isNaN(num)) return "Height must be a number.";
    if (unit === "ft") {
      // reasonable human height in feet (1.5ft is too small, should be ~4.5ft - but unit selection likely uses ft as feet)
      if (num < 3 || num > 10) return "Enter a valid height in feet (e.g. 4.5).";
    } else {
      // cm
      if (num < 50 || num > 300) return "Enter a valid height in cm (50 - 300).";
    }
    return "";
  };

  const validateWeight = (value, unit) => {
    if (value === "" || value === null || typeof value === "undefined") return "";
    const num = Number(value);
    if (Number.isNaN(num)) return "Weight must be a number.";
    if (unit === "lbs") {
      if (num < 44 || num > 1100) return "Enter a valid weight in lbs.";
    } else {
      // kg
      if (num < 20 || num > 500) return "Enter a valid weight in kg (20 - 500).";
    }
    return "";
  };

  const handleFetchProfileData = async () => {
    try {
      const resp = await getUserProfile(user.id);

      console.log("User Profile:", resp);

      // Map API response to formData structure
      setFormData((prev) => ({
        ...prev,
        name: resp.data.name || "",
        email: resp.data.email || "",
        phone_number: resp.data.profile?.phone_number || "",
        height: resp.data.profile?.height || "",
        height_unit: resp.data.profile?.height_unit || height_unit || "cm",
        weight: resp.data.profile?.weight || "",
        weight_unit: resp.data.profile?.weight_unit || weight_unit || "kg",
        body_type: resp.data.profile?.body_type || "",
        // profilePhoto stays as is (user uploads)
      }));
    } catch (err) {
      console.error("Error fetching user profile:", err);
    } finally {
      setInitialLoading(false); // Stop loading spinner
    }
  };

  // ✅ Load profile data on component mount (like InvoiceSection)
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (user?.id) {
      handleFetchProfileData();
    }
  }, [user]);
  /* eslint-enable react-hooks/exhaustive-deps */

  useEffect(() => {
    return () => {
      if (formData.profilePhoto) {
        URL.revokeObjectURL(formData.profilePhoto);
      }
    };
  }, [formData.profilePhoto]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Inline validation for height/weight when user types
    if (name === "height") {
      const err = validateHeight(value, formData.height_unit);
      setFormErrors((prev) => ({ ...prev, height: err }));
    }
    if (name === "weight") {
      const err = validateWeight(value, formData.weight_unit);
      setFormErrors((prev) => ({ ...prev, weight: err }));
    }
    // Re-validate when the unit is changed (height_unit/weight_unit)
    if (name === "height_unit") {
      const err = validateHeight(formData.height, value);
      setFormErrors((prev) => ({ ...prev, height: err }));
    }
    if (name === "weight_unit") {
      const err = validateWeight(formData.weight, value);
      setFormErrors((prev) => ({ ...prev, weight: err }));
    }
  };

  // Enforce numeric-only input and sensible max lengths for height/weight
  const sanitizeNumberInput = (name, rawValue, unit) => {
    if (!rawValue && rawValue !== 0) return "";
    // allow digits and at most one dot for 'ft' (feet), otherwise only digits
    let cleaned = String(rawValue);
    // normalize: keep digits and dots initially
    cleaned = cleaned.replace(/[^0-9.]/g, "");

    // collapse multiple dots to a single dot (keep first)
    const firstDot = cleaned.indexOf(".");
    if (firstDot !== -1) {
      cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, "");
    }

    // If user typed leading dot, convert to '0.' so typing is smoother
    if (cleaned === ".") cleaned = "0.";

    if (unit === "ft") {
      // limit overall length to avoid very large inputs (e.g. 10.5)
      if (cleaned.length > 4) cleaned = cleaned.slice(0, 4);
      // allow trailing dot (e.g. '5.') while typing
      return cleaned;
    }

    if (name === "weight") {
      // allow one decimal with up to 2 fractional digits
      const parts = cleaned.split(".");
      const intPart = (parts[0] || "").replace(/^0+(?=\d)/, "");
      const fracPart = parts[1] || "";
      const intLimited = intPart.slice(0, 3);
      const fracLimited = fracPart.slice(0, 2);
      // If there is a dot but no frac yet, keep the trailing dot to allow typing fraction
      if (cleaned.includes(".") && fracLimited === "") {
        return `${intLimited}.`;
      }
      return fracLimited ? `${intLimited}.${fracLimited}` : intLimited;
    }

    // height in cm: integers only, max 3 digits
    cleaned = cleaned.replace(/\D/g, "");
    if (cleaned.length > 3) cleaned = cleaned.slice(0, 3);
    if (cleaned.length > 1) cleaned = cleaned.replace(/^0+/, "");
    return cleaned;
  };

  const handleNumberChange = (e) => {
    const { name } = e.target;
    // Determine unit for sanitization
    const unit = name === "height" ? formData.height_unit : formData.weight_unit;
    const raw = e.target.value;
    const cleaned = sanitizeNumberInput(name, raw, unit);
    setFormData((prev) => ({ ...prev, [name]: cleaned }));
    // Run inline validation
    if (name === "height") {
      setFormErrors((prev) => ({ ...prev, height: validateHeight(cleaned, unit) }));
    }
    if (name === "weight") {
      setFormErrors((prev) => ({ ...prev, weight: validateWeight(cleaned, unit) }));
    }
  };

  const handlePhoneChange = (phone) => {
    setFormData((prev) => ({
      ...prev,
      phone_number: phone,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const { name, email, phone_number } = formData;

    if (!name || !email || !phone_number) {
      notify.error("Please fill in all required fields.");
      setIsLoading(false);
      return;
    }

    const emailRegex = /^\S+@\S+\.\S+$/;
    if (!emailRegex.test(email)) {
      notify.error("Please enter a valid email address.");
      setIsLoading(false);
      return;
    }

    const phoneDigits = phone_number.replace(/\D/g, "");
    if (phoneDigits.length < 10 || phoneDigits.length > 15) {
      notify.error("Please enter a valid phone number with country code.");
      setIsLoading(false);
      return;
    }

    // Require height and weight
    const heightVal = formData.height;
    const weightVal = formData.weight;
    const missingErrors = { height: "", weight: "" };
    let missing = false;
    if (!heightVal) {
      missingErrors.height = "Height is required.";
      missing = true;
    }
    if (!weightVal) {
      missingErrors.weight = "Weight is required.";
      missing = true;
    }
    if (missing) {
      setFormErrors(missingErrors);
      notify.error("Please fill in required fields (height and weight).");
      setIsLoading(false);
      return;
    }

    // Final validation for height and weight
    const heightErr = validateHeight(formData.height, formData.height_unit);
    const weightErr = validateWeight(formData.weight, formData.weight_unit);
    if (heightErr || weightErr) {
      setFormErrors({ height: heightErr, weight: weightErr });
      notify.error("Please fix the highlighted errors before submitting the form.");
      setIsLoading(false);
      return;
    }

    try {
      const submitData = new FormData();

      if (formData.profilePhoto) {
        const response = await fetch(formData.profilePhoto);
        const blob = await response.blob();
        const file = new File([blob], "profile-photo.jpg", {
          type: "image/jpeg",
        });
        submitData.append("profilePhoto", file);
      }

      Object.keys(formData).forEach((key) => {
        if (key !== "profilePhoto") {
          submitData.append(key, formData[key]);
        }
      });

      const resp = await updateUserProfile(user.id, submitData);
      if (resp.success) {

        notify.success(resp.message);
      }

    } catch (error) {
      console.error("Error submitting form:", error);
      notify.error("Error updating profile. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // keep handler for future photo-upload UI (may be currently commented out)
  // eslint-disable-next-line no-unused-vars
  const handlePhotoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        notify.error("Please select a valid image file.");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        notify.error("File size must be less than 5MB.");
        return;
      }
      setFormData((prev) => ({
        ...prev,
        profilePhoto: URL.createObjectURL(file),
      }));
    }
  };

  // ✅ Show loading spinner while initial data is being fetched
  if (initialLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading Profile...</p>
      </div>
    );
  }

  return (
    <form
      className={`profile-form ${isLoading ? "form-loading" : ""}`}
      onSubmit={handleSubmit}
    >
      {/* Profile Photo Section */}
      {/* <div className="form-section photo-upload-section">
        <h3 className="pro-section-title">Profile Photo</h3>
        <div className="photo-upload-container">
          <Avatar
            src={formData.profilePhoto}
            className="profile-photo"
            sx={{
              width: 120,
              height: 120,
              bgcolor: "var(--primary-color)",
            }}
          >
            {!formData.profilePhoto && <AddAPhotoIcon sx={{ fontSize: 40 }} />}
          </Avatar>
          <div
            className="photo-upload-overlay"
            onClick={() => document.getElementById("photo-upload").click()}
          >
            <AddAPhotoIcon className="photo-upload-icon" />
          </div>
          <input
            id="photo-upload"
            type="file"
            accept="image/*"
            onChange={handlePhotoChange}
            className="photo-upload-input"
          />
        </div>
        <p
          style={{
            color: "var(--text-secondary)",
            fontSize: "0.9rem",
            margin: "0.5rem 0 0",
          }}
        >
          Click to upload profile photo (Max 5MB)
        </p>
      </div> */}

      {/* Basic Information */}
      <div className="form-section">
        <h3 className="pro-section-title">Basic Information</h3>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label required">Full Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your full name"
              className="form-input"
              required
              error={formErrors.name}
            />
          </div>

          <div className="form-group">
            <label className="form-label required">Email (you can't change this)</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              className="form-input"
              required
              readOnly
              disabled
              error={formErrors.email}
            />
          </div>

          <div className="form-group full-width">
            <label className="form-label required">Phone Number</label>
            <PhoneInput
              country={"au"}
              value={formData.phone_number}
              onChange={handlePhoneChange}
              placeholder="Enter phone number"
              inputClass="form-control"
              containerClass="react-tel-input"
              required
            />
          </div>
        </div>
      </div>

      {/* Physical Information */}
      <div className="form-section">
        <h3 className="pro-section-title">Physical Information</h3>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Height</label>
            <div className="measurement-group">
              <input
                type="text"
                name="height"
                value={formData.height}
                onChange={handleNumberChange}
                placeholder="Enter height (e.g. 4.5)"
                className="form-input measurement-input"
                inputMode="decimal"
                required
                error={formErrors.height}
              />
              <select
                name="height_unit"
                value={formData.height_unit}
                onChange={handleChange}
                className="form-select measurement-unit"
              >
                <option value="cm">cm</option>
                <option value="ft">ft</option>
              </select>
            </div>
            {formErrors.height && (
              <div className="field-error" style={{ color: "#d32f2f", marginTop: "0.4rem" }}>
                {formErrors.height}
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Weight</label>
            <div className="measurement-group">
              <input
                type="text"
                name="weight"
                value={formData.weight}
                onChange={handleNumberChange}
                placeholder="Enter weight (e.g. 72.5)"
                className="form-input measurement-input"
                inputMode="decimal"
                pattern="^\d{1,3}(?:\.\d{1,2})?$"
                required
              />
              <select
                name="weight_unit"
                value={formData.weight_unit}
                onChange={handleChange}
                className="form-select measurement-unit"
              >
                <option value="kg">kg</option>
                <option value="lbs">lbs</option>
              </select>
            </div>
            {formErrors.weight && (
              <div className="field-error" style={{ color: "#d32f2f", marginTop: "0.4rem" }}>
                {formErrors.weight}
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Body Type</label>
            <select
              name="body_type"
              value={formData.body_type}
              onChange={handleChange}
              className="form-select"
              required
            >
              <option value="">Select body type</option>
              {bodyTypeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <button type="submit" className="submit-btn" disabled={isLoading}>
        {isLoading ? (
          <>
            <span className="loading">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="36"
                height="36"
                viewBox="0 0 24 24"
              >
                <g
                  fill="none"
                  stroke="#000"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                >
                  <path
                    strokeDasharray="2 4"
                    strokeDashoffset="6"
                    d="M12 21c-4.97 0 -9 -4.03 -9 -9c0 -4.97 4.03 -9 9 -9"
                  >
                    <animate
                      attributeName="stroke-dashoffset"
                      dur="0.6s"
                      repeatCount="indefinite"
                      values="6;0"
                    />
                  </path>
                  <path
                    strokeDasharray="32"
                    strokeDashoffset="32"
                    d="M12 3c4.97 0 9 4.03 9 9c0 4.97 -4.03 9 -9 9"
                  >
                    <animate
                      fill="freeze"
                      attributeName="stroke-dashoffset"
                      begin="0.1s"
                      dur="0.4s"
                      values="32;0"
                    />
                  </path>
                  <path
                    strokeDasharray="10"
                    strokeDashoffset="10"
                    d="M12 16v-7.5"
                  >
                    <animate
                      fill="freeze"
                      attributeName="stroke-dashoffset"
                      begin="0.5s"
                      dur="0.2s"
                      values="10;0"
                    />
                  </path>
                  <path
                    strokeDasharray="6"
                    strokeDashoffset="6"
                    d="M12 8.5l3.5 3.5M12 8.5l-3.5 3.5"
                  >
                    <animate
                      fill="freeze"
                      attributeName="stroke-dashoffset"
                      begin="0.7s"
                      dur="0.2s"
                      values="6;0"
                    />
                  </path>
                </g>
              </svg>
            </span>
            Saving Changes...
          </>
        ) : (
          <>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 16 16"
            >
              <path
                fill="#000"
                d="M8.5 1.5A1.5 1.5 0 0 1 10 0h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h6c-.314.418-.5.937-.5 1.5v7.793L4.854 6.646a.5.5 0 1 0-.708.708l3.5 3.5a.5.5 0 0 0 .708 0l3.5-3.5a.5.5 0 0 0-.708-.708L8.5 9.293z"
              />
            </svg>
            {"Save Changes"}
          </>
        )}
      </button>
    </form>
  );
};

export default ProfileForm;
